import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Snackbar,
  Typography,
  Avatar,
  Paper,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SubmitBtn, Label, TextInput, CustomBtn } from "../styled/auth";
import Loading from "../utilities/Loading";
import styled from "styled-components";
import { handleUserLogin } from "../../redux/reducers/authReducer";
import { unwrapResult } from "@reduxjs/toolkit";
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const LoginCard = styled(Paper)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  margin: 40px 20px;
  justify-content: center;
  align-items: center;
  gap: 32px;
  width: 500px;
  max-width: calc(100% - 40px);
  min-height: 500px;
  border-radius: 24px;
  padding: 40px 32px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow:
      0 25px 50px rgba(0, 0, 0, 0.15),
      0 0 0 1px rgba(255, 255, 255, 0.1);
  }

  @media (min-width: 1200px) {
    width: 480px;
    min-height: 600px;
    border-radius: 28px;
    padding: 48px 40px;
  }

  @media (max-width: 480px) {
    margin: 20px 16px;
    width: calc(100% - 32px);
    min-height: auto;
    border-radius: 20px;
    padding: 24px 20px;
    gap: 24px;
  }
`;

const LogoContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
`;

const StyledAvatar = styled(Avatar)`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 3px solid rgba(255, 255, 255, 0.8);
  background: white;

  img {
    object-fit: contain;
    border-radius: 16px;
  }
`;

const FormContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
`;

const InputGroup = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const ButtonContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-top: 8px;
`;

const BackButtonContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-top: 16px;

  &::before {
    content: "";
    height: 1px;
    width: 60%;
    background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
    margin-bottom: 8px;
  }
`;

export default function UserLogin(props) {
  const [error, setError] = useState("");
  const { setLoading, setStep, loading = false } = props;
  const navigate = useNavigate();
  const { companyData } = useSelector((state) => ({
    companyData: state.Auth.companyData,
  }));
  const dispatch = useDispatch();
  const { t } = useTranslation("auth");
  const handleStepBack = () => {
    setStep(1);
  };
  const handleClose = () => {
    setError(false);
  };
  const submitForm = (data) => {
    dispatch(
      handleUserLogin({
        data: data,
        setLoading: setLoading,
      }),
    )
      .then(unwrapResult)
      .catch((err) => {
        setLoading(false);
        setError("UserLogin", { message: t("Login.UserName_Error") });
      });
  };
  const schema = yup
    .object({
      username: yup.string().required(t("Login.UserName_Error")),
      password: yup.string().required(t("Login.Password_Error")),
    })
    .required();
  const methods = useForm({
    mode: "onTouched",
    defaultValues: {
      username: "",
      password: "",
    },
    resolver: yupResolver(schema),
  });
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = methods;

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitForm)}>
          <LoginCard elevation={0}>
            <LogoContainer>
              <StyledAvatar
                src={
                  companyData.Logo ||
                  `https://ahmedaglan.github.io/ClientsJSON/ClientImages/${
                    companyData.ClientID || "999999"
                  }/logo.png`
                }
                alt="Company Logo"
              />
              <Typography
                variant="body2"
                sx={{ color: "#64748b", fontWeight: 500 }}
              >
                {t("Login.welcome", { company: companyData.CompanyName })}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: "#1e293b",
                  fontWeight: 700,
                  fontSize: { xs: "24px", sm: "28px", md: "32px" },
                  lineHeight: 1.2,
                }}
              >
                {t("Login.Login_Header")}
              </Typography>
            </LogoContainer>

            <FormContainer>
              {error && (
                <Snackbar
                  open={!!error}
                  autoHideDuration={6000}
                  onClose={handleClose}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                  <Alert
                    onClose={handleClose}
                    severity="error"
                    sx={{
                      width: "100%",
                      borderRadius: "12px",
                      background:
                        "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      color: "white",
                    }}
                  >
                    {t("Login.error_message")}
                  </Alert>
                </Snackbar>
              )}

              <InputGroup>
                <Label htmlFor="username">{t("Login.UserName_Label")}</Label>
                <TextInput
                  id="username"
                  name="username"
                  variant="outlined"
                  placeholder={t("Login.UserName_Placeholder")}
                  {...register("username")}
                  error={!!errors.username}
                  helperText={errors.username?.message}
                />
              </InputGroup>

              <InputGroup>
                <Label htmlFor="password">{t("Login.Password_Label")}</Label>
                <TextInput
                  id="password"
                  type="password"
                  name="password"
                  variant="outlined"
                  placeholder={t("Login.Password_Placeholder")}
                  {...register("password")}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                />
              </InputGroup>
            </FormContainer>

            <ButtonContainer>
              <SubmitBtn type="submit" disabled={loading}>
                {loading ? (
                  <Loading
                    isButtonLoading={true}
                    buttonText={t("Login.Login_Submit")}
                    spinnerType="2"
                  />
                ) : (
                  t("Login.Login_Submit")
                )}
              </SubmitBtn>
            </ButtonContainer>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                mt: 2,
              }}
            >
              <Button
                variant="text"
                onClick={() => navigate("/auth/forgot-password")}
                sx={{ color: "#64748b", fontWeight: 600 }}
              >
                {t("Login.Forgot_Password") || "نسيت كلمة السر؟"}
              </Button>
            </Box>

            {!companyData.defined && (
              <BackButtonContainer>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontWeight: 600,
                    textAlign: "center",
                  }}
                >
                  {t("Login.other_header")}
                </Typography>
                <CustomBtn onClick={handleStepBack}>
                  {t("Login.step_back")}
                </CustomBtn>
              </BackButtonContainer>
            )}
          </LoginCard>
        </form>
      </FormProvider>
    </>
  );
}
